import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO, generateOrganizationSchema, generateWebSiteSchema, generateServiceSchema } from '../../utils/seo';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [contactFormStatus, setContactFormStatus] = useState('');

  // Generate combined structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      generateOrganizationSchema(),
      generateWebSiteSchema(),
      generateServiceSchema()
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lang: string) => {
    console.log('Language switched to:', lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  // Get current language from i18n
  const currentLang = i18n.language;

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const textarea = formData.get('projectDetails') as string;
    if (textarea && textarea.length > 500) {
      setContactFormStatus(t('contact.form.status.maxLength') || 'Project details must be 500 characters or less');
      return;
    }
    
    setContactFormStatus(t('contact.form.status.sending') || 'Sending...');
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d53u0frontabvievttug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });
      
      if (response.ok) {
        setContactFormStatus(t('contact.form.status.success') || 'Thank you! We will contact you soon.');
        form.reset();
        setTimeout(() => setContactFormStatus(''), 5000);
      } else {
        setContactFormStatus(t('contact.form.status.error') || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setContactFormStatus(t('contact.form.status.networkError') || 'Network error. Please try again.');
    }
  };

  const services = [
    {
      icon: 'ri-sun-line',
      title: t('services.solar.title') || 'Solar Energy Systems',
      description: t('services.solar.description') || 'Utility-scale and commercial solar PV engineering services, including feasibility studies, system design, performance modeling, and optimization for energy yield and project economics.'
    },
    {
      icon: 'ri-windy-line',
      title: t('services.wind.title') || 'Wind Power Solutions',
      description: t('services.wind.description') || 'Advanced wind farm development support, including turbine selection and performance analysis for onshore and offshore projects.'
    },
    {
      icon: 'ri-battery-charge-line',
      title: t('services.battery.title') || 'Battery Storage',
      description: t('services.battery.description') || 'Energy storage system integration, BESS design, and grid-scale battery solutions to enhance reliability and flexibility.'
    },
    {
      icon: 'ri-flashlight-line',
      title: t('services.grid.title') || 'Grid Integration',
      description: t('services.grid.description') || 'Power system studies, grid connection engineering, and compliance solutions for seamless renewable energy integration.'
    }
  ];

  const projects = [
    {
      image: 'https://public.readdy.ai/ai/img_res/cc1666270c0eed618a095d42a85a36b2.jpg',
      category: t('projects.solar.category') || 'Solar Energy',
      title: t('projects.solar.title') || 'Utility-Scale Solar PV — Engineering Support',
      scope: t('projects.solar.scope') || 'Scope includes feasibility studies, PV system design, yield modeling, and optimization.'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=modern%20offshore%20wind%20turbines%20farm%20in%20ocean%20water%20white%20wind%20turbines%20with%20three%20blades%20against%20blue%20sky%20and%20calm%20sea%20renewable%20energy%20infrastructure%20clean%20simple%20background%20professional%20engineering%20photography%20bright%20natural%20lighting%20high%20quality%20detailed%20image&width=400&height=320&seq=sal002&orientation=landscape',
      category: t('projects.wind.category') || 'Wind Power',
      title: t('projects.wind.title') || 'Wind Energy — Resource Assessment & Yield Analysis',
      scope: t('projects.wind.scope') || 'Scope includes resource assessment, layout optimization, turbine selection support, and energy yield analysis.'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=large%20scale%20battery%20energy%20storage%20system%20facility%20with%20white%20container%20units%20and%20modern%20industrial%20equipment%20clean%20organized%20layout%20renewable%20energy%20infrastructure%20simple%20background%20professional%20engineering%20photography%20bright%20daylight%20high%20quality%20detailed%20image&width=400&height=320&seq=sal003&orientation=landscape',
      category: t('projects.storage.category') || 'Energy Storage',
      title: t('projects.storage.title') || 'Battery Energy Storage — Grid Integration & Sizing',
      scope: t('projects.storage.scope') || 'Scope includes BESS sizing, grid integration studies, operating scenarios, and performance evaluation.'
    }
  ];

  const expertise = [
    { icon: 'ri-line-chart-line', title: t('expertise.items.powerSystems.title') || 'Power Systems Analysis', description: t('expertise.items.powerSystems.description') || 'Load flow, stability, and fault analysis for complex power networks' },
    { icon: 'ri-leaf-line', title: t('expertise.items.environmental.title') || 'Environmental Impact', description: t('expertise.items.environmental.description') || 'Comprehensive environmental assessments and sustainability reporting' },
    { icon: 'ri-shield-check-line', title: t('expertise.items.gridCompliance.title') || 'Grid Compliance', description: t('expertise.items.gridCompliance.description') || 'Regulatory compliance and grid code adherence consulting' },
    { icon: 'ri-money-dollar-circle-line', title: t('expertise.items.financial.title') || 'Financial Modeling', description: t('expertise.items.financial.description') || 'Economic analysis, ROI projections, and investment optimization' },
    { icon: 'ri-team-line', title: t('expertise.items.projectManagement.title') || 'Project Management', description: t('expertise.items.projectManagement.description') || 'End-to-end project delivery from concept to commissioning' },
    { icon: 'ri-file-list-3-line', title: t('expertise.items.regulatory.title') || 'Regulatory Consulting', description: t('expertise.items.regulatory.description') || 'Permitting, licensing, and regulatory strategy development' }
  ];

  return (
    <>
      <SEO
        title="SALL Engineering | Renewable Energy Engineering Istanbul"
        description="SALL Engineering delivers advanced renewable energy engineering and consulting services from Istanbul, Türkiye. Specializing in solar PV systems, wind power solutions, battery energy storage, and grid integration across global markets."
        keywords="renewable energy engineering, solar energy consulting, wind power solutions, battery storage systems, grid integration"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-white">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <img src="https://public.readdy.ai/ai/img_res/9627a3c2-cdf6-4a10-bcaf-5daa34df9421.png" alt="SALL Engineering" className="h-12 w-auto" />
            </div>
            <div className="hidden md:flex items-center space-x-10">
              <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.home') || 'Home'}</a>
              <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.about') || 'About Us'}</a>
              <a href="#services" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.services') || 'Our Services'}</a>
              <a href="#projects" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.projects') || 'Projects'}</a>
              <a href="#expertise" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.expertise') || 'Expertise'}</a>
              <a href="#contact" className={`text-sm font-medium transition-colors cursor-pointer ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-300'}`}>{t('nav.contact') || 'Contact'}</a>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full p-1">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    currentLang === 'en'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : isScrolled
                      ? 'text-gray-600 hover:text-gray-900'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('it')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    currentLang === 'it'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : isScrolled
                      ? 'text-gray-600 hover:text-gray-900'
                      : 'text-white/80 hover:text-white'
                  }`}
                >
                  IT
                </button>
              </div>
              <a href="#contact" className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer">
                {t('nav.requestConsultation') || 'Request Consultation'}
              </a>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-teal-900 to-white"></div>
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-transparent"></div>
          
          <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
            <div className="max-w-3xl">
              <h1 className="text-7xl font-bold text-white mb-12">
                {t('hero.title') || 'Engineering the Future of Renewable Energy'}
              </h1>
              <p className="text-xl text-white/90 font-light mb-10 max-w-xl leading-relaxed">
                {t('hero.subtitle') || 'with Global Expertise in Solar, Wind & Storage'}
              </p>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-4">{t('services.sectionLabel') || 'Our Engineering Capabilities'}</p>
              <h2 className="text-5xl font-extrabold text-gray-900">{t('services.sectionTitle') || 'Integrated Renewable Energy Engineering Solutions'}</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
                  onMouseEnter={() => setActiveService(index)}
                >
                  <div className="w-16 h-16 flex items-center justify-center mb-8">
                    <i className={`${service.icon} text-6xl text-blue-500 group-hover:scale-110 transition-transform`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                  <p className="text-base text-gray-600 leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-36 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-6">{t('about.sectionLabel') || 'ABOUT SALL ENGINEERING'}</p>
                <h2 className="text-5xl font-bold text-gray-900 mb-10">{t('about.sectionTitle') || 'Global Engineering Excellence in Renewable Energy Solutions'}</h2>
                
                <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-lg">
                  {t('about.paragraph1') || 'SALL Engineering is the renewable energy engineering and consulting division of SALL Collection Tekstil Sanayi ve Ticaret A.Ş., headquartered in Istanbul, Türkiye. The company delivers advanced technical solutions for solar, wind, and energy storage projects across global markets.'}
                </p>
                
                <div className="bg-gray-50 border-l-4 border-teal-600 p-4 max-w-lg">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <span className="font-semibold text-gray-800">{t('about.noteLabel') || 'Corporate Note:'}</span> {t('about.noteText') || 'SALL Engineering operates as part of SALL Collection Tekstil Sanayi ve Ticaret A.Ş., a diversified holding company with activities across multiple industries.'}
                  </p>
                </div>
              </div>
              
              <div className="relative h-[600px]">
                <div className="absolute bottom-0 right-0 w-[420px] h-[560px] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=modern%20large%20scale%20solar%20farm%20installation%20with%20rows%20of%20photovoltaic%20panels%20and%20engineers%20working%20on%20site%20renewable%20energy%20infrastructure%20professional%20engineering%20scene%20clean%20simple%20background%20bright%20daylight%20high%20quality%20detailed%20vertical%20image&width=420&height=560&seq=sal004&orientation=portrait" 
                    alt="Solar Farm Installation" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute top-0 left-0 w-[280px] h-[280px] rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://readdy.ai/api/search-image?query=professional%20engineering%20team%20with%20hard%20hats%20reviewing%20technical%20blueprints%20and%20plans%20at%20renewable%20energy%20construction%20site%20diverse%20engineers%20collaboration%20clean%20simple%20background%20bright%20natural%20lighting%20high%20quality%20detailed%20square%20image&width=280&height=280&seq=sal005&orientation=squarish" 
                    alt="Engineering Team" 
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects" className="py-32 bg-amber-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-4">{t('projects.sectionLabel') || 'FEATURED PROJECTS'}</p>
              <h2 className="text-5xl font-extrabold text-gray-900">{t('projects.sectionTitle') || 'Selected Experience in Renewable Energy Projects'}</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
              {projects.map((project, index) => (
                <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                  <div className="w-full h-80 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={project.title} 
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-8">
                    <span className="inline-block bg-teal-600 text-white text-xs px-3 py-1 rounded-full mb-4">
                      {project.category}
                    </span>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3 line-clamp-2">{project.title}</h3>
                    <p className="text-base text-gray-600">
                      {project.scope}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Expertise Section */}
        <section id="expertise" className="py-36 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
              <div className="lg:col-span-2">
                <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-6">{t('expertise.sectionLabel') || 'TECHNICAL EXPERTISE'}</p>
                <h2 className="text-5xl font-extrabold text-gray-900 mb-8 max-w-md leading-tight">{t('expertise.sectionTitle') || 'Multidisciplinary Engineering Capabilities'}</h2>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('expertise.description') || 'Our team brings together specialists in electrical engineering, mechanical design, environmental science, and project management to deliver comprehensive renewable energy solutions that meet the highest technical and regulatory standards.'}
                </p>
              </div>
              
              <div className="lg:col-span-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {expertise.map((item, index) => (
                    <div key={index} className="bg-gray-100 rounded-2xl p-7 hover:bg-gray-200 transition-colors cursor-pointer">
                      <div className="w-8 h-8 flex items-center justify-center mb-4">
                        <i className={`${item.icon} text-3xl text-teal-600`}></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=modern%20renewable%20energy%20facility%20with%20solar%20panels%20in%20foreground%20and%20wind%20turbines%20in%20distance%20during%20golden%20hour%20sunset%20warm%20orange%20and%20golden%20lighting%20clean%20energy%20infrastructure%20panoramic%20view%20simple%20background%20professional%20photography%20high%20quality%20detailed%20landscape%20image&width=1920&height=600&seq=sal006&orientation=landscape" 
              alt="Renewable Energy Facility" 
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-black/40"></div>
          
          <div className="relative max-w-7xl mx-auto px-6 w-full">
            <div className="flex justify-between items-end">
              <div className="max-w-lg">
                <h2 className="text-6xl font-light text-white mb-8 leading-tight">
                  {t('cta.title') || 'Ready to discuss your next project?'}
                </h2>
                <a href="#contact" className="inline-flex items-center bg-white hover:bg-gray-100 text-gray-900 px-8 py-4 rounded-full text-base font-semibold transition-all cursor-pointer whitespace-nowrap group">
                  {t('cta.button') || 'Contact Us'}
                  <span className="ml-3 w-10 h-10 bg-black rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-white text-lg"></i>
                  </span>
                </a>
              </div>
              
              <div className="max-w-md text-right">
                <p className="text-2xl text-white font-light leading-relaxed">
                  {t('cta.description') || 'Partner with SALL Engineering for world-class consulting and engineering services in renewable energy.'}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-32 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-16">
              <p className="text-teal-600 text-sm font-semibold uppercase tracking-wider mb-4">{t('contact.sectionLabel') || 'GET IN TOUCH'}</p>
              <h2 className="text-5xl font-extrabold text-gray-900 mb-6">{t('contact.sectionTitle') || "Let's Discuss Your Project"}</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('contact.description') || "Whether you're planning a new renewable energy installation or need expert consulting for an existing project, our team is ready to help."}
              </p>
            </div>
            
            <form className="bg-gray-50 rounded-3xl p-12 shadow-lg" data-readdy-form id="contact-form" onSubmit={handleContactSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.fullName') || 'Full Name'} {t('contact.form.required') || '*'}</label>
                  <input 
                    type="text" 
                    name="fullName"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder={t('contact.form.placeholders.fullName') || 'John Smith'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.company') || 'Company'}</label>
                  <input 
                    type="text" 
                    name="company"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder={t('contact.form.placeholders.company') || 'Your Company'}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.email') || 'Email'} {t('contact.form.required') || '*'}</label>
                  <input 
                    type="email" 
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder={t('contact.form.placeholders.email') || 'john@company.com'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.phone') || 'Phone'}</label>
                  <input 
                    type="tel" 
                    name="phone"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm"
                    placeholder={t('contact.form.placeholders.phone') || '+1 (555) 123-4567'}
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.serviceInterest') || 'Service Interest'}</label>
                <select 
                  name="serviceInterest"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm cursor-pointer"
                >
                  <option value="">{t('contact.form.serviceOptions.select') || 'Select a service'}</option>
                  <option value="Solar Energy Systems">{t('contact.form.serviceOptions.solar') || 'Solar Energy Systems'}</option>
                  <option value="Wind Power Solutions">{t('contact.form.serviceOptions.wind') || 'Wind Power Solutions'}</option>
                  <option value="Battery Storage">{t('contact.form.serviceOptions.battery') || 'Battery Storage'}</option>
                  <option value="Grid Integration">{t('contact.form.serviceOptions.grid') || 'Grid Integration'}</option>
                  <option value="General Consulting">{t('contact.form.serviceOptions.general') || 'General Consulting'}</option>
                </select>
              </div>
              
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-900 mb-2">{t('contact.form.projectDetails') || 'Project Details'} {t('contact.form.required') || '*'}</label>
                <textarea 
                  name="projectDetails"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all resize-none text-sm"
                  placeholder={t('contact.form.placeholders.projectDetails') || "Tell us about your project requirements, timeline, and any specific challenges you're facing..."}
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">{t('contact.form.maxCharacters') || 'Maximum 500 characters'}</p>
              </div>
              
              {contactFormStatus && (
                <div className={`mb-6 p-4 rounded-lg text-center text-sm font-medium ${
                  contactFormStatus.includes(t('contact.form.status.success') || 'Thank you') 
                    ? 'bg-green-100 text-green-800' 
                    : contactFormStatus.includes(t('contact.form.status.sending') || 'Sending') 
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {contactFormStatus}
                </div>
              )}
              
              <button 
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-full text-base font-semibold transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('contact.form.submit') || 'Submit Inquiry'}
              </button>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black text-white rounded-t-3xl mx-[6%] mt-20">
          <div className="max-w-7xl mx-auto px-12 py-20">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
              <div className="md:col-span-4">
                <h4 className="text-sm text-gray-500 uppercase font-semibold mb-6">{t('footer.quickLinks') || 'Quick Links'}</h4>
                <div className="space-y-4">
                  <a href="#about" className="block text-base text-white underline hover:text-teal-400 transition-colors cursor-pointer">{t('nav.about') || 'About Us'}</a>
                  <a href="#services" className="block text-base text-white underline hover:text-teal-400 transition-colors cursor-pointer">{t('nav.services') || 'Services'}</a>
                  <a href="#projects" className="block text-base text-white underline hover:text-teal-400 transition-colors cursor-pointer">{t('nav.projects') || 'Projects'}</a>
                </div>
              </div>
              
              <div className="md:col-span-4">
                <h4 className="text-sm text-gray-500 uppercase font-semibold mb-5">{t('footer.contactTitle') || 'Contact'}</h4>
                <p className="text-base text-white mb-10">{t('footer.email') || 'info@sallengineering.com'}</p>
              </div>
            </div>
            
            <div className="border-t border-gray-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <p className="text-sm text-gray-500">{t('footer.copyright') || '© 2025 SALL Engineering. All rights reserved.'}</p>
                
                <div className="flex gap-6">
                  <Link to="/privacy" className="text-sm text-white underline hover:text-teal-400 transition-colors cursor-pointer">{t('footer.privacy') || 'Privacy Policy'}</Link>
                  <Link to="/terms" className="text-sm text-white underline hover:text-teal-400 transition-colors cursor-pointer">{t('footer.terms') || 'Terms of Service'}</Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
